import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Tag,
  InputNumber,
  Radio,
  Tabs,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';
import type { ColumnsType } from 'antd/es/table';
import {
  getAssessmentResultTag,
  getAssessmentScoreColor,
  getAssessmentStatusTag,
  normalizeAssessment,
  num,
  unwrapList,
} from './supplierAssessmentUi';

const { Option } = Select;
const { TextArea } = Input;

interface AssessmentTemplate {
  id: string;
  template_code: string;
  template_name: string;
  category: string;
  version: string;
  passing_score: number;
  is_active: boolean;
}

interface Assessment {
  id: string;
  assessment_number: string;
  supplier_id?: string;
  supplier_name: string;
  template_name: string;
  assessment_date: string;
  status: string;
  score_percentage: number;
  assessment_result: string;
  total_score: number;
  max_possible_score: number;
  approved_at?: string | null;
}

interface SupplierOption {
  id: string;
  supplier_name: string;
  supplier_code?: string;
}

const SupplierAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AssessmentTemplate | null>(null);

  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [questionModalVisible, setQuestionModalVisible] = useState(false);
  const [createAssessmentModalVisible, setCreateAssessmentModalVisible] = useState(false);

  const [templateForm] = Form.useForm();
  const [questionForm] = Form.useForm();
  const [createAssessmentForm] = Form.useForm();

  useEffect(() => {
    fetchTemplates();
    fetchAssessments();
    fetchSuppliers();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/procurement/supplier-assessment-templates');
      setTemplates(unwrapList<AssessmentTemplate>(response));
    } catch (error) {
      message.error('Failed to fetch templates');
    }
  };

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/procurement/supplier-assessments');
      setAssessments(unwrapList<Assessment>(response).map((a) => normalizeAssessment(a)));
    } catch (error) {
      message.error('Failed to fetch assessments');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/procurement/suppliers', {
        params: { is_active: 'true' },
      });
      setSuppliers(unwrapList<SupplierOption>(response));
    } catch (error) {
      message.error('Failed to fetch suppliers');
    }
  };

  const handleCreateTemplate = async (values: any) => {
    try {
      await apiClient.post('/procurement/supplier-assessment-templates', {
        template_code: values.template_code,
        template_name: values.template_name,
        category: values.category,
        version: values.version || '1.0',
        scoring_method: values.scoring_method || 'weighted',
        passing_score: values.passing_score || 70,
        description: values.description,
      });
      message.success('Template created successfully');
      setTemplateModalVisible(false);
      templateForm.resetFields();
      fetchTemplates();
    } catch (error) {
      message.error('Failed to create template');
    }
  };

  const handleAddQuestion = async (values: any) => {
    if (!selectedTemplate) return;

    try {
      await apiClient.post(
        `/procurement/supplier-assessment-templates/${selectedTemplate.id}/questions`,
        {
          question_category: values.question_category,
          question_text: values.question_text,
          question_type: values.question_type,
          max_score: values.max_score,
          weight: values.weight || 1.0,
          is_mandatory: values.is_mandatory || false,
          expected_answer: values.expected_answer,
          scoring_criteria: values.scoring_criteria,
        }
      );
      message.success('Question added successfully');
      setQuestionModalVisible(false);
      questionForm.resetFields();
    } catch (error) {
      message.error('Failed to add question');
    }
  };

  const handleCreateAssessment = async (values: any) => {
    try {
      const response = await apiClient.post('/procurement/supplier-assessments', {
        supplier_id: values.supplier_id,
        template_id: values.template_id,
        assessment_type: values.assessment_type || 'Initial',
        assessment_date: values.assessment_date.format('YYYY-MM-DD'),
        assessor_name: values.assessor_name,
        assessment_scope: values.assessment_scope,
        assessment_location: values.assessment_location,
      });
      message.success('Assessment created successfully');
      setCreateAssessmentModalVisible(false);
      createAssessmentForm.resetFields();
      const created =
        response && typeof response === 'object' && 'data' in response
          ? (response as { data: any }).data
          : response;
      const createdId = created?.id || created?.data?.id;
      if (createdId) {
        navigate(`/scm/quality/supplier-assessments/${createdId}`);
        return;
      }
      fetchAssessments();
    } catch (error: any) {
      const detail =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Failed to create assessment';
      message.error(detail);
    }
  };

  const templateColumns: ColumnsType<AssessmentTemplate> = [
    {
      title: 'Template Code',
      dataIndex: 'template_code',
      key: 'template_code',
      width: 150,
    },
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 250,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
      width: 100,
    },
    {
      title: 'Passing Score',
      dataIndex: 'passing_score',
      key: 'passing_score',
      width: 120,
      render: (score) => `${score}%`,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active) => (
        <Tag color={active ? 'green' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedTemplate(record);
              setQuestionModalVisible(true);
            }}
          >
            Add Question
          </Button>
        </Space>
      ),
    },
  ];

  const assessmentColumns: ColumnsType<Assessment> = [
    {
      title: 'Assessment No.',
      dataIndex: 'assessment_number',
      key: 'assessment_number',
      width: 150,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
    },
    {
      title: 'Template',
      dataIndex: 'template_name',
      key: 'template_name',
      width: 200,
    },
    {
      title: 'Assessment Date',
      dataIndex: 'assessment_date',
      key: 'assessment_date',
      width: 120,
      render: (date) => dayjs(date).format('DD MMM YYYY'),
    },
    {
      title: 'Score',
      key: 'score',
      width: 150,
      render: (_, record) => (
        <div>
          <Progress
            percent={num(record.score_percentage)}
            size="small"
            strokeColor={getAssessmentScoreColor(record.score_percentage)}
            format={(percent) => `${num(percent).toFixed(1)}%`}
          />
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {num(record.total_score)}/{num(record.max_possible_score, 100)}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status) => getAssessmentStatusTag(status),
    },
    {
      title: 'Result',
      dataIndex: 'assessment_result',
      key: 'assessment_result',
      width: 150,
      render: (result) => (result ? getAssessmentResultTag(result) : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/scm/quality/supplier-assessments/${record.id}`)}
          >
            Details
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Supplier Assessment</h1>
          <p style={{ color: '#8c8c8c' }}>
            Manage assessment templates, conduct evaluations, and approve suppliers
          </p>
        </div>
        <Space>
          <Button icon={<PlusOutlined />} onClick={() => setTemplateModalVisible(true)}>
            Create Template
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              fetchSuppliers();
              setCreateAssessmentModalVisible(true);
            }}
          >
            Create Assessment
          </Button>
        </Space>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: '1',
            label: `Assessments (${assessments.length})`,
            children: (
              <Card>
                <Table
                  columns={assessmentColumns}
                  dataSource={assessments}
                  rowKey="id"
                  loading={loading}
                  pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                  scroll={{ x: 1400 }}
                />
              </Card>
            ),
          },
          {
            key: '2',
            label: `Templates (${templates.length})`,
            children: (
              <Card>
                <Table
                  columns={templateColumns}
                  dataSource={templates}
                  rowKey="id"
                  pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* Create Template Modal */}
      <Modal
        title="Create Assessment Template"
        open={templateModalVisible}
        onCancel={() => {
          setTemplateModalVisible(false);
          templateForm.resetFields();
        }}
        onOk={() => templateForm.submit()}
        width={600}
      >
        <Form form={templateForm} layout="vertical" onFinish={handleCreateTemplate}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="template_code"
                label="Template Code"
                rules={[{ required: true, message: 'Please enter code' }]}
              >
                <Input placeholder="e.g., SA-001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="version" label="Version" initialValue="1.0">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="template_name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., General Supplier Assessment" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select placeholder="Select category">
                  <Option value="General">General</Option>
                  <Option value="Manufacturing">Manufacturing</Option>
                  <Option value="Services">Services</Option>
                  <Option value="Materials">Materials</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="passing_score" label="Passing Score (%)" initialValue={70}>
                <InputNumber style={{ width: '100%' }} min={0} max={100} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="scoring_method" label="Scoring Method" initialValue="weighted">
            <Radio.Group>
              <Radio value="weighted">Weighted</Radio>
              <Radio value="simple">Simple Average</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Template description..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Question Modal */}
      <Modal
        title={`Add Question to ${selectedTemplate?.template_name || 'Template'}`}
        open={questionModalVisible}
        onCancel={() => {
          setQuestionModalVisible(false);
          questionForm.resetFields();
          setSelectedTemplate(null);
        }}
        onOk={() => questionForm.submit()}
        width={700}
      >
        <Form form={questionForm} layout="vertical" onFinish={handleAddQuestion}>
          <Form.Item
            name="question_category"
            label="Category"
            rules={[{ required: true, message: 'Please select category' }]}
          >
            <Select placeholder="Select category">
              <Option value="Quality-Management">Quality Management</Option>
              <Option value="Documentation">Documentation</Option>
              <Option value="Process-Control">Process Control</Option>
              <Option value="Equipment">Equipment</Option>
              <Option value="Personnel">Personnel</Option>
              <Option value="Delivery">Delivery</Option>
              <Option value="Financial">Financial</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="question_text"
            label="Question"
            rules={[{ required: true, message: 'Please enter question' }]}
          >
            <TextArea rows={3} placeholder="Enter the assessment question..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="question_type"
                label="Question Type"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select type">
                  <Option value="Yes-No">Yes/No</Option>
                  <Option value="Rating">Rating (1-5)</Option>
                  <Option value="Score">Score (0-100)</Option>
                  <Option value="Text">Text Response</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="max_score"
                label="Max Score"
                rules={[{ required: true }]}
                initialValue={5}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="weight" label="Weight" initialValue={1.0}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_mandatory" valuePropName="checked" label=" ">
                <input type="checkbox" /> Mandatory Question
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="expected_answer" label="Expected Answer">
            <Input placeholder="Expected answer for reference..." />
          </Form.Item>

          <Form.Item name="scoring_criteria" label="Scoring Criteria">
            <TextArea rows={3} placeholder="Define scoring criteria..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Assessment Modal */}
      <Modal
        title="Create Supplier Assessment"
        open={createAssessmentModalVisible}
        onCancel={() => {
          setCreateAssessmentModalVisible(false);
          createAssessmentForm.resetFields();
        }}
        onOk={() => createAssessmentForm.submit()}
        width={600}
      >
        <Form form={createAssessmentForm} layout="vertical" onFinish={handleCreateAssessment}>
          <Form.Item
            name="supplier_id"
            label="Supplier"
            rules={[{ required: true, message: 'Please select supplier' }]}
          >
            <Select
              placeholder="Select Supplier"
              showSearch
              optionFilterProp="label"
              options={suppliers.map((s) => ({
                value: s.id,
                label: s.supplier_code
                  ? `${s.supplier_name} (${s.supplier_code})`
                  : s.supplier_name,
              }))}
              notFoundContent={suppliers.length === 0 ? 'No active suppliers found' : undefined}
            />
          </Form.Item>

          <Form.Item
            name="template_id"
            label="Assessment Template"
            rules={[{ required: true, message: 'Please select template' }]}
          >
            <Select placeholder="Select Template">
              {templates.map((template) => (
                <Option key={template.id} value={template.id}>
                  {template.template_name} (v{template.version})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="assessment_type"
            label="Assessment Type"
            initialValue="Initial"
            rules={[{ required: true, message: 'Please select assessment type' }]}
          >
            <Select placeholder="Select type">
              <Option value="Initial">Initial</Option>
              <Option value="Re-evaluation">Re-evaluation</Option>
              <Option value="Special-audit">Special audit</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="assessment_date"
            label="Assessment Date"
            rules={[{ required: true, message: 'Please select date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="assessor_name"
            label="Assessor Name"
            rules={[{ required: true, message: 'Please enter assessor name' }]}
          >
            <Input placeholder="Name of assessor" />
          </Form.Item>

          <Form.Item name="assessment_scope" label="Assessment Scope">
            <TextArea rows={3} placeholder="Scope of assessment..." />
          </Form.Item>

          <Form.Item name="assessment_location" label="Location">
            <Input placeholder="Assessment location" />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default SupplierAssessmentPage;
